import { Platform } from '@angular/cdk/platform';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import type { OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  output,
  inject,
  input,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import type { VirtualScrollerComponent, IPageInfo } from '@iharbeck/ngx-virtual-scroller';
import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { startWith } from 'rxjs/operators';

import { NgTemplateAssertType } from '@core/common-legacy';
import { KeyboardEventCode } from '@core/common-legacy/keyboard';
import type {
  EmojiListNavigationKeys,
  SymbolRow,
  SymbolVirtualScrollRow,
  SymbolSelectItem,
  EmojiItem,
  SymbolDataSource,
  SymbolGroup,
  SymbolItem,
} from '@core/symbol-data';
import { SymbolVirtualScrollItem, SymbolDataSourceId } from '@core/symbol-data';
import {
  ButtonComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownWidth,
  FormFieldComponent,
  IconComponent,
  InputComponent,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';
import { debounceAnimationFrame } from '@core/utils-legacy';

import { EmojiComponent } from '../emoji/emoji.component';
import { IconFontComponent } from '../icon-font/icon-font.component';
import { injectSymbolPickerTranslations } from '../symbol-translations.service';

interface SymbolTemplateType<T> {
  symbol: SymbolItem<T>;
}

export const PICKER_ROW_SIZE = 9;
export const datasourceFilterFn = (
  dataSource: SymbolDataSource<unknown>,
  query: string,
): SymbolDataSource<unknown> => ({
  ...dataSource,
  groups: dataSource.groups.reduce(
    (acc, group) => {
      const data = group.data.filter(symbol => symbol.searchTerm.includes(query));
      if (!data.length) return acc;

      return acc.concat({ label: group.label, data });
    },
    <Array<SymbolGroup<unknown>>>[],
  ),
});

@Component({
  selector: 'rt-symbol-picker',
  templateUrl: './symbol-picker.component.html',
  styleUrls: ['./symbol-picker.component.less'],
  host: {
    '(keyup)': 'onKeyUp($event)',
  },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IconFontComponent,
    EmojiComponent,
    TabComponent,
    TabsComponent,
    FormFieldComponent,
    InputComponent,
    IconComponent,
    VirtualScrollerModule,
    DropdownHeaderComponent,
    NgTemplateOutlet,
    NgClass,
    ReactiveFormsModule,
    DropdownComponent,
    NgTemplateAssertType,
    ButtonComponent,
  ],
})
export class SymbolPickerComponent implements OnInit, OnDestroy {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly platform = inject(Platform);

  protected readonly translations = injectSymbolPickerTranslations();

  public readonly SymbolVirtualScrollItem = SymbolVirtualScrollItem;
  public readonly EmojiTemplateType: SymbolTemplateType<EmojiItem>;
  public readonly IconTemplateType: SymbolTemplateType<string>;

  public readonly withSearchInput = input(true);
  public readonly searchValue = input<string>(undefined!);

  @Input() public dataSources: Array<SymbolDataSource<unknown>>;
  @Input() public hasClearButton = false;

  @Output() public select = new EventEmitter<SymbolSelectItem>();
  public clear = output<void>();

  @ViewChild('emojiTemplate', { static: true }) public emojiTemplate: TemplateRef<
    SymbolTemplateType<EmojiItem>
  >;
  @ViewChild('iconTemplate', { static: true }) public iconTemplate: TemplateRef<
    SymbolTemplateType<string>
  >;

  public templateIdMap: Record<string, TemplateRef<SymbolTemplateType<unknown>>>;
  public currentDataSource: SymbolDataSource<unknown>;
  public isScrollAlwaysVisible = false;
  public virtualScrollRows: SymbolVirtualScrollRow;
  public virtualScrollData: SymbolVirtualScrollRow;
  public searchFormControl = new FormControl<string>('', { nonNullable: true });
  public activeRow = 1;
  public activeCol = 0;
  public startIndexWithBuffer = 0;

  public DropdownWidth = DropdownWidth;

  @ViewChild('virtualScrollerComponent', { static: true })
  private virtualScrollerComponent: VirtualScrollerComponent;
  private coalesceChangeDetection = debounceAnimationFrame(() => this.cdRef.detectChanges());

  public ngOnInit(): void {
    this.templateIdMap = {
      [SymbolDataSourceId.Emoji]: this.emojiTemplate,
      [SymbolDataSourceId.MaterialIcons]: this.iconTemplate,
    };

    this.searchFormControl.valueChanges
      .pipe(startWith(this.searchFormControl.value))
      .subscribe(value => {
        const filteredDataSource = datasourceFilterFn(this.currentDataSource, value);
        this.virtualScrollRows = this.dataSourceToVirtualScrollRows(filteredDataSource);
      });
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.searchValue) {
      this.searchFormControl.setValue(this.searchValue());
    }

    if (changes.dataSources) {
      this.currentDataSource = this.dataSources[0];
    }
  }

  public ngOnDestroy() {
    this.coalesceChangeDetection.destroy();
    this.select.complete();
  }

  public onMouseEnter(rowIndex: number, colIndex: number) {
    this.activeRow = rowIndex;
    this.activeCol = colIndex;
    this.cdRef.detectChanges();
  }

  public onKeyUp(event: KeyboardEvent) {
    this.handleKey(<EmojiListNavigationKeys>event.key);
  }

  public handleKey(code: EmojiListNavigationKeys) {
    switch (code) {
      case KeyboardEventCode.ArrowUp: {
        this.onKeyPressArrowUp();
        break;
      }
      case KeyboardEventCode.ArrowDown: {
        this.onKeyPressArrowDown();
        break;
      }
      case KeyboardEventCode.ArrowRight: {
        this.onKeyPressArrowRight();
        break;
      }
      case KeyboardEventCode.ArrowLeft: {
        this.onKeyPressArrowLeft();
        break;
      }
      case KeyboardEventCode.Enter: {
        this.onKeyPressArrowEnter();
        break;
      }
      default:
        break;
    }

    this.cdRef.detectChanges();
  }

  public listUpdate(data: SymbolVirtualScrollRow) {
    this.virtualScrollData = data;
    this.isScrollAlwaysVisible = (this.platform.FIREFOX || this.platform.SAFARI) && data.length > 6;
    this.cdRef.detectChanges();
  }

  public onChange(event: IPageInfo) {
    this.startIndexWithBuffer = event.startIndexWithBuffer;
    this.coalesceChangeDetection();
  }

  public onSelect(symbolValue: unknown) {
    this.select.emit(<SymbolSelectItem>{
      dataSourceId: this.currentDataSource.id,
      value: symbolValue,
    });
  }

  public onDataSourceChange(dataSource: SymbolDataSource<any>) {
    this.currentDataSource = dataSource;
    this.searchFormControl.setValue('');
    this.cdRef.detectChanges();
  }

  private dataSourceToVirtualScrollRows(
    dataSource: SymbolDataSource<unknown>,
  ): SymbolVirtualScrollRow {
    return dataSource.groups.reduce(
      (acc, group) => {
        const items = group.data;
        if (!items.length) return acc;

        let chunks: SymbolVirtualScrollRow = [];
        let index = 0;
        while (index < items.length) {
          chunks = [
            ...chunks,
            {
              type: SymbolVirtualScrollItem.SymbolRow,
              data: items.slice(index, index + PICKER_ROW_SIZE),
            },
          ];
          index += PICKER_ROW_SIZE;
        }

        return acc.concat({ type: SymbolVirtualScrollItem.Heading, name: group.label }, chunks);
      },
      <SymbolVirtualScrollRow>[],
    );
  }

  private onKeyPressArrowUp() {
    let nextRowIndex = this.activeRow - 1;
    while (this.virtualScrollRows[nextRowIndex]?.type === SymbolVirtualScrollItem.Heading)
      nextRowIndex--;

    this.activeRow = nextRowIndex < 0 ? this.activeRow : nextRowIndex;
    const row = <SymbolRow>this.virtualScrollRows[this.activeRow];
    this.activeCol = this.activeCol < row.data.length ? this.activeCol : row.data.length - 1;

    const { startIndex, startIndexWithBuffer } = this.virtualScrollerComponent.viewPortInfo;
    if (this.activeRow - startIndexWithBuffer <= startIndex - startIndexWithBuffer) {
      this.virtualScrollerComponent.scrollToIndex(this.activeRow, true, -36, 0);
    }
  }

  private onKeyPressArrowDown() {
    let nextRowIndex = this.activeRow + 1;
    while (this.virtualScrollRows[nextRowIndex]?.type === SymbolVirtualScrollItem.Heading)
      nextRowIndex++;

    this.activeRow = nextRowIndex < this.virtualScrollRows.length ? nextRowIndex : this.activeRow;
    const row = <SymbolRow>this.virtualScrollRows[this.activeRow];
    this.activeCol = this.activeCol < row.data.length ? this.activeCol : row.data.length - 1;

    const { endIndex, startIndexWithBuffer } = this.virtualScrollerComponent.viewPortInfo;
    if (this.activeRow - startIndexWithBuffer >= endIndex - startIndexWithBuffer) {
      this.virtualScrollerComponent.scrollToIndex(this.activeRow, false, 100, 0);
    }
  }

  private onKeyPressArrowRight() {
    this.activeCol = (this.activeCol + 1) % PICKER_ROW_SIZE;
    const row = <SymbolRow>this.virtualScrollRows[this.activeRow];
    this.activeCol = this.activeCol < row.data.length ? this.activeCol : 0;
  }

  private onKeyPressArrowLeft() {
    const row = <SymbolRow>this.virtualScrollRows[this.activeRow];
    this.activeCol = (this.activeCol === 0 ? row.data.length : this.activeCol) - 1;
  }

  private onKeyPressArrowEnter() {
    const row = <SymbolRow>this.virtualScrollRows[this.activeRow];
    this.onSelect(row.data[this.activeCol].value);
  }
}
